import { RowActionMenuItem } from '@approvalmax/ui/src/components';
import { useVendorTemplateFieldSettings } from 'pages/requestForm/containers/netsuite/hooks';
import { useCallback, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { FormValues } from '../../NetSuiteVendorCreationPopupContent.types';
import { getColumnDefinition } from './AddressLinesInformation.config';
import { messages } from './AddressLinesInformation.messages';

export const useAddressTable = () => {
    const fieldSettings = useVendorTemplateFieldSettings();

    const { control, getValues } = useFormContext<FormValues>();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'addressLines',
    });

    const handleRowAdd = useCallback(() => {
        const addressLinesValues = getValues('addressLines');
        const hasAddressLines = addressLinesValues?.length !== 0;

        append({
            defaultBilling: !hasAddressLines,
            defaultShipping: !hasAddressLines,
        });
    }, [append, getValues]);

    const columnData = useMemo(() => {
        return fields.map((field, index) => ({
            ...field,
            index,
        }));
    }, [fields]);

    const handleCloneLine = useCallback(
        (index: number) => {
            const addressLinesValues = getValues('addressLines');

            const data = addressLinesValues?.[index] ?? {};

            append({
                ...data,
                defaultBilling: false,
                defaultShipping: false,
            });
        },
        [append, getValues]
    );

    const columns = useMemo(() => getColumnDefinition(control, fieldSettings), [control, fieldSettings]);

    const rowActionMenuItems = useMemo<RowActionMenuItem<(typeof columnData)[number]>[]>(
        () => [
            {
                label: messages.cloneLine,
                onClick: (item) => handleCloneLine(item.index),
            },
            {
                label: messages.removeLine,
                onClick: (item) => remove(item.index),
            },
        ],
        [handleCloneLine, remove]
    );

    return {
        handleRowAdd,
        columnData,
        columns,
        rowActionMenuItems,
    };
};
